import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:4000';

let accessToken = localStorage.getItem('access') || null;
let refreshToken = localStorage.getItem('refresh') || null;

export function setTokens({ access, refresh }) {
  if (access) {
    accessToken = access;
    localStorage.setItem('access', access);
  }
  if (refresh) {
    refreshToken = refresh;
    localStorage.setItem('refresh', refresh);
  }

  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('tokens:updated'));
    }
  } catch {
    // ignore
  }
}

export function clearTokens() {
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem('access');
  localStorage.removeItem('refresh');

  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('tokens:updated'));
    }
  } catch {
    // ignore
  }
}

const http = axios.create({
  baseURL: API_BASE,
});

/** Do not send a stale JWT on routes that create a new session (avoids confusion and useless 401 handling). */
function shouldOmitAccessToken(urlPath) {
  const path = String(urlPath || '').split('?')[0];
  if (path === '/auth/google' || path === '/auth/login') return true;
  if (path === '/auth/refresh') return true;
  return false;
}

http.interceptors.request.use((config) => {
  const path = String(config?.url || '').split('?')[0];
  if (accessToken && !shouldOmitAccessToken(path)) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  // Admin archive mode: force semestre scope via query param
  // Backend resolveSemestreScope reads req.query.semestre_id for admin users
  try {
    const url = String(config?.url || '');
    const isAuthRoute = url.startsWith('/auth');
    const isSemestresRoute = url.startsWith('/semestres');
    const override = localStorage.getItem('semestre_id_override');
    if (!isAuthRoute && !isSemestresRoute && override) {
      config.params = config.params || {};
      if (config.params.semestre_id == null || String(config.params.semestre_id).trim() === '') {
        config.params.semestre_id = String(override);
      }
    }
  } catch {
    // ignore
  }

  return config;
});

let isRefreshing = false;
let pendingQueue = [];

async function refreshAccess() {
  if (!refreshToken) throw new Error('no-refresh');
  const { data } = await axios.post(`${API_BASE}/auth/refresh`, { refresh: refreshToken });
  setTokens({ access: data.access });
  return data.access;
}

http.interceptors.response.use(
  (res) => {
    try {
      const method = String(res?.config?.method || 'get').toLowerCase();
      const isMutation = method === 'post' || method === 'put' || method === 'patch' || method === 'delete';
      if (isMutation && typeof window !== 'undefined') {
        window.dispatchEvent(new Event('badges:refresh'));
      }
    } catch {
      // ignore
    }
    return res;
  },
  async (error) => {
    const original = error.config || {};
    const url = original.url || '';
    const isAuthRoute = url.startsWith('/auth');
    const allowRefreshOnAuthRoute = url === '/auth/ping' || url === '/auth/logout';

    if (error.response && error.response.status === 401 && !original._retry && (!isAuthRoute || allowRefreshOnAuthRoute)) {
      if (!refreshToken) {
        return Promise.reject(error);
      }
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newAccess = await refreshAccess();
          pendingQueue.forEach((cb) => cb(newAccess));
          pendingQueue = [];
        } catch (e) {
          pendingQueue = [];
          clearTokens();
        } finally {
          isRefreshing = false;
        }
      }
      return new Promise((resolve, reject) => {
        pendingQueue.push((token) => {
          if (!token) return reject(error);
          original._retry = true;
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${token}`;
          resolve(axios(original));
        });
      });
    }
    return Promise.reject(error);
  }
);

export default http;
